import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

const satisfactionOptions = [
  "Very Satisfied",
  "Satisfied",
  "Neither",
  "Dissatisfied",
  "Very Dissatisfied",
];

const questions = [
  "Services are easily accessible",
  "Service providers are available when I need them",
  "Staff is courteous and professional",
  "Wait times are reasonable",
  "My concerns are addressed promptly",
  "Communication is clear and helpful",
  "The service environment is clean and comfortable",
  "Scheduling is easy and flexible",
  "Overall satisfaction with services",
];

const validationSchema = Yup.object(
  questions.reduce((acc, _, index) => {
    acc[`q-${index}`] = Yup.string().required("Required");
    return acc;
  }, {})
);

export default function Survey() {
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      ...questions.reduce((acc, _, index) => {
        acc[`q-${index}`] = "";
        return acc;
      }, {}),
      comment: "",
      user: "",
      unit: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const { user, unit, comment, ...questionResponses } = values;
    
      const responses = Object.entries(questionResponses).reduce((acc, [key, value]) => {
        const index = parseInt(key.split("-")[1]);
        acc[questions[index]] = value;
        return acc;
      }, {});
    
      const payload = { user, unit, comment, responses };
    
      console.log("🔄 Sending survey payload:", payload);
    
      try {
        const res = await fetch("http://localhost:4499/questa/v1/surveys/bsas/new", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          cache: "no-store", // ensure no cache issues
          body: JSON.stringify(payload),
        });
    
        const contentType = res.headers.get("content-type");
        let responseBody;
    
        if (contentType && contentType.includes("application/json")) {
          responseBody = await res.json();
        } else {
          responseBody = await res.text(); // fallback
        }
    
        if (!res.ok) {
          throw new Error(
            responseBody?.message || `Request failed with status ${res.status}`
          );
        }
    
        alert("✅ Thank you for submitting your feedback!");
        resetForm();
        navigate("/bsas");
      } catch (err) {
        console.error("🚨 Submission Error:", err);
        alert("❌ Submission failed: " + err.message);
      }
    }
  });

  return (
    <div className="p-6 max-w-6xl mx-auto text-white">
      <h2 className="text-2xl font-bold mb-4 text-center text-[#3490dc]">
        BSAS Quality Assurance Survey
      </h2>
      <form onSubmit={formik.handleSubmit}>
        {/* Optional User and Unit Inputs */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 text-[#3490dc] font-bold">
              Your Name (optional)
            </label>
            <input
              type="text"
              name="user"
              value={formik.values.user}
              onChange={formik.handleChange}
              className="w-full p-2 rounded bg-gray-800 text-white"
              placeholder="Enter your name"
            />
          </div>
          <div>
            <label className="block mb-1 text-[#3490dc] font-bold">
              Unit/Department (optional)
            </label>
            <input
              type="text"
              name="unit"
              value={formik.values.unit}
              onChange={formik.handleChange}
              className="w-full p-2 rounded bg-gray-800 text-white"
              placeholder="Enter your unit"
            />
          </div>
        </div>

        {/* Satisfaction Matrix Table */}
        <table className="w-full border border-gray-300 text-sm text-center">
          <thead className="bg-gray-700">
            <tr>
              <th className="p-2 text-left">Question</th>
              {satisfactionOptions.map((option) => (
                <th key={option} className="p-2">
                  {option}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {questions.map((question, qIndex) => {
              const fieldName = `q-${qIndex}`;
              const error = formik.touched[fieldName] && formik.errors[fieldName];
              return (
                <tr
                  key={qIndex}
                  className={`${
                    error ? "bg-red-100 text-red-700" : "odd:bg-gray-800 even:bg-gray-900"
                  }`}
                >
                  <td className="p-2 text-left">
                    {question}
                    {error && (
                      <div className="text-xs text-red-400">
                        {formik.errors[fieldName]}
                      </div>
                    )}
                  </td>
                  {satisfactionOptions.map((option) => (
                    <td key={option} className="p-2">
                      <input
                        type="radio"
                        name={fieldName}
                        value={option}
                        checked={formik.values[fieldName] === option}
                        onChange={formik.handleChange}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Optional Comment */}
        <div className="mt-6">
          <label className="text-[#3490dc] font-bold block mb-2">
            Tell Us How we did?
          </label>
          <textarea
            name="comment"
            className="w-full h-[125px] text-[#3490dc] p-2 rounded bg-gray-800"
            placeholder="Comment"
            value={formik.values.comment}
            onChange={formik.handleChange}
          />
        </div>

        {/* Submit Button */}
        <div className="mt-6 text-center">
          <button
            type="submit"
            className="bg-blue-600 px-6 py-2 rounded text-white hover:bg-blue-700"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}
